import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Calendar, Clock, User, Mail, Phone, FileText } from 'lucide-react';
import { Product, AppointmentInput, TimeSlot } from '../types';
import { useTranslation } from 'react-i18next';
import { BookedSlot, getBookedSlots } from '../lib/api';

interface AppointmentFormProps {
  product: Product;
  onClose: () => void;
  onSubmit: (data: AppointmentInput) => void;
}

const timeSlots: TimeSlot[] = [
  { id: 'morning', label: 'Morning', time: '9:00 AM - 12:00 PM' },
  { id: 'afternoon', label: 'Afternoon', time: '1:00 PM - 4:00 PM' },
  { id: 'evening', label: 'Evening', time: '5:00 PM - 8:00 PM' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s().-]{5,}$/;

type FieldKey = 'name' | 'email' | 'phone' | 'appointmentDate' | 'timeSlot';
type Errors = Partial<Record<FieldKey, string>>;

export function AppointmentForm({ product, onClose, onSubmit }: AppointmentFormProps) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<AppointmentInput>({
    name: '',
    email: '',
    phone: '',
    appointmentDate: '',
    timeSlot: '',
    notes: '',
    productId: product.id,
    website: '',
  });
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [booked, setBooked] = useState<BookedSlot[]>([]);
  // Anti-bot. Frozen at mount so the server can measure how long the user
  // actually spent on the form. Real humans take seconds; bots submit instantly.
  const formOpenedAt = useRef<number>(Date.now());

  useEffect(() => {
    getBookedSlots().then(setBooked).catch(() => setBooked([]));
  }, []);

  const bookedByDate = useMemo(() => {
    const map = new Map<string, Set<string>>();
    booked.forEach((b) => {
      if (!map.has(b.date)) map.set(b.date, new Set());
      map.get(b.date)!.add(b.timeSlot);
    });
    return map;
  }, [booked]);

  const errors = useMemo<Errors>(() => {
    const e: Errors = {};
    if (formData.name.trim().length < 2) e.name = t('form.errors.nameTooShort');
    if (!EMAIL_RE.test(formData.email)) e.email = t('form.errors.emailInvalid');
    if (!PHONE_RE.test(formData.phone)) e.phone = t('form.errors.phoneInvalid');
    if (!formData.appointmentDate) e.appointmentDate = t('form.errors.dateRequired');
    if (!formData.timeSlot) e.timeSlot = t('form.errors.timeRequired');
    return e;
  }, [formData, t]);

  function showError(field: FieldKey): string | undefined {
    if (!submitAttempted && !touched[field]) return undefined;
    return errors[field];
  }

  function fieldClass(field: FieldKey, base: string) {
    const err = showError(field);
    // text-gray-900 is explicit: the modal is white, but the page inherits the
    // theme's --ink (near-white on the dark themes), which would render typed
    // text almost invisible.
    return `${base} text-gray-900 ${err ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (Object.keys(errors).length > 0) return;
    // Attach anti-bot fields at submission time. `website` is the honeypot
    // (always empty for real users — bots that auto-fill every input will
    // populate it). `formOpenedAt` is the epoch-ms at modal mount so the
    // server can reject sub-2-second submissions (instant bots).
    onSubmit({
      ...formData,
      formOpenedAt: formOpenedAt.current,
    });
  };

  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full relative animate-fadeIn overflow-y-auto max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4">{t('form.title')}</h2>

          <div className="bg-indigo-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold mb-2">{t('form.about.title')}</h3>
            <p className="text-sm text-gray-600 mb-2">{t('form.about.description')}</p>
            <p className="text-sm text-gray-600">
              <span className="font-semibold">{t('form.about.duration')}:</span>{' '}
              {product.measurementDuration}
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <User className="w-4 h-4 inline-block mr-2" />
                {t('form.fields.name')}
              </label>
              <input
                type="text"
                className={fieldClass('name', 'w-full px-3 py-2 border rounded-md focus:ring-2')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                onBlur={() => setTouched((p) => ({ ...p, name: true }))}
                aria-invalid={Boolean(showError('name'))}
              />
              {showError('name') && (
                <p className="mt-1 text-xs text-red-600">{showError('name')}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline-block mr-2" />
                  {t('form.fields.email')}
                </label>
                <input
                  type="email"
                  className={fieldClass('email', 'w-full px-3 py-2 border rounded-md focus:ring-2')}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  onBlur={() => setTouched((p) => ({ ...p, email: true }))}
                  aria-invalid={Boolean(showError('email'))}
                />
                {showError('email') && (
                  <p className="mt-1 text-xs text-red-600">{showError('email')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="w-4 h-4 inline-block mr-2" />
                  {t('form.fields.phone')}
                </label>
                <input
                  type="tel"
                  className={fieldClass('phone', 'w-full px-3 py-2 border rounded-md focus:ring-2')}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  onBlur={() => setTouched((p) => ({ ...p, phone: true }))}
                  aria-invalid={Boolean(showError('phone'))}
                />
                {showError('phone') && (
                  <p className="mt-1 text-xs text-red-600">{showError('phone')}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline-block mr-2" />
                  {t('form.fields.date')}
                </label>
                <select
                  className={fieldClass('appointmentDate', 'w-full px-3 py-2 border rounded-md focus:ring-2')}
                  value={formData.appointmentDate}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    const stillFree =
                      !formData.timeSlot || !bookedByDate.get(newDate)?.has(formData.timeSlot);
                    setFormData({
                      ...formData,
                      appointmentDate: newDate,
                      timeSlot: stillFree ? formData.timeSlot : '',
                    });
                  }}
                  onBlur={() => setTouched((p) => ({ ...p, appointmentDate: true }))}
                  aria-invalid={Boolean(showError('appointmentDate'))}
                >
                  <option value="">{t('form.fields.date')}</option>
                  {getAvailableDates().map((date) => {
                    const taken = bookedByDate.get(date)?.size ?? 0;
                    const fullyBooked = taken >= timeSlots.length;
                    return (
                      <option key={date} value={date} disabled={fullyBooked}>
                        {new Date(date).toLocaleDateString(undefined, {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                        {fullyBooked ? ` — ${t('form.slots.fullyBooked')}` : ''}
                      </option>
                    );
                  })}
                </select>
                {showError('appointmentDate') && (
                  <p className="mt-1 text-xs text-red-600">{showError('appointmentDate')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4 inline-block mr-2" />
                  {t('form.fields.time')}
                </label>
                <select
                  className={fieldClass('timeSlot', 'w-full px-3 py-2 border rounded-md focus:ring-2')}
                  value={formData.timeSlot}
                  onChange={(e) => setFormData({ ...formData, timeSlot: e.target.value })}
                  onBlur={() => setTouched((p) => ({ ...p, timeSlot: true }))}
                  aria-invalid={Boolean(showError('timeSlot'))}
                >
                  <option value="">{t('form.fields.time')}</option>
                  {timeSlots.map((slot) => {
                    const taken =
                      formData.appointmentDate &&
                      bookedByDate.get(formData.appointmentDate)?.has(slot.id);
                    return (
                      <option key={slot.id} value={slot.id} disabled={Boolean(taken)}>
                        {t(`form.timeSlots.${slot.id}`)} ({slot.time})
                        {taken ? ` — ${t('form.slots.taken')}` : ''}
                      </option>
                    );
                  })}
                </select>
                {showError('timeSlot') && (
                  <p className="mt-1 text-xs text-red-600">{showError('timeSlot')}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FileText className="w-4 h-4 inline-block mr-2" />
                {t('form.fields.notes')}
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('form.fields.notesPlaceholder')}
              />
            </div>

            {/*
              Honeypot anti-bot field. Hidden from real users via aria + CSS
              + tabIndex=-1 so keyboard/screen-reader users skip it entirely.
              A bot that auto-fills every input populates it; the server then
              rejects the booking silently. Label is intentionally plausible
              so naive bots don't pattern-match the trap.
            */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-10000px',
                top: 'auto',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
              }}
            >
              <label htmlFor="appt-website">Website (leave blank)</label>
              <input
                id="appt-website"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={formData.website ?? ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                {t('form.buttons.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                {t('form.buttons.confirm')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
